import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import * as _ from 'lodash-es';
import { Subject } from 'rxjs';
import { GspLoggerService } from 'src/app/log-handler.service';
import { LayersStreams } from 'src/app/shared/common/current-layers/layers-streams.service';
import { EnvironmentService } from 'src/app/shared/common/environment/environment.service';
import { FeatureFilter } from 'src/app/shared/common/feature-filter/feature-filter';
import { ThrottledHttpService } from 'src/app/shared/common/utility/throttled-http.service';
import { CachedFeatureService } from 'src/app/shared/map-data-services/feature/cached-feature.service';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { Layer } from 'src/app/shared/map-data-services/layer/layer';
import { MapJob } from 'src/app/shared/survey-data-services/survey-job';

import { FullFeatureFilter } from 'src/app/shared/common/feature-filter/full-feature-filter';

import { SymbologyOverrideChoice } from 'src/app/shared/map-data-services/styles/style-symbology';
import { UserSettingsStreamService } from 'src/app/shared/user/user-settings-stream.service';
import { MapContentMonitor } from '../../common/map-content-monitor.service';
import { TemplatedFeatureMetadataProperty } from '../../side-panel/feature-panel/feature-fields/templated-feature';
import { TILE_CONFIG } from '../tile-config';
import { ClusterLayerService, markerClusterGroupOptions } from './cluster-layer.service';
import { FeatureMapLayer } from './feature-map-layer';
import { FeatureMapLayerCacheService } from './feature-map-layer-cache.service';
import { FeatureMapLayersService } from './feature-map-layers.service';

const tileLayerOptions = {
    clipTiles: true,
    noWrap: true, // needed to ensure the same tile is not loaded twice resulting in duplicate features and clusters
    minZoom: 0,
    maxZoom: 21,

    // Tile size must match TILE_CONFIG.tileSizeParam for correct coordinate mapping
    // See tile-config.ts for detailed explanation
    tileSize: TILE_CONFIG.tileSizePx,
    zoomOffset: 0
};

@Injectable({
    providedIn: 'root'
})
export class TileClusterLayerService {
    constructor(
        cluster: ClusterLayerService,
        private env: EnvironmentService,
        private throttledHttp: ThrottledHttpService,
        private featureMapLayersService: FeatureMapLayersService,
        private featureMapLayerCacheService: FeatureMapLayerCacheService,
        private cachedFeatureService: CachedFeatureService,
        private layersStreams: LayersStreams,
        private mapContentMonitor: MapContentMonitor,
        private logger: GspLoggerService,
        private userSettingsStreamService: UserSettingsStreamService
    ) {
        if (!L.ClusterLayer) {
            cluster.extendLeaflet();
        }
    }

    // This function needs to be called before L.TileClusterLayer can be used
    extendLeaflet(): void {
        this._extendLeaflet(
            this.env,
            this.throttledHttp,
            this.featureMapLayersService,
            this.featureMapLayerCacheService,
            this.cachedFeatureService,
            this.layersStreams,
            this.logger,
            this.userSettingsStreamService
        );
    }

    _extendLeaflet(
        env: EnvironmentService,
        throttledHttp: ThrottledHttpService,
        featureMapLayersService: FeatureMapLayersService,
        featureMapLayerCacheService: FeatureMapLayerCacheService,
        cachedFeatureService: CachedFeatureService,
        layersStreams: LayersStreams,
        log: GspLoggerService,
        userSettingsStreamService: UserSettingsStreamService
    ): void {
        (L as any).TileClusterLayer = L.ClusterLayer.extend({
            initialize: function (
                map: L.Map,
                projectId: string,
                mapWorkspaceId: string,
                layerModel: Layer | MapJob,
                zIndex: number,
                applyFilter: FeatureFilter,
                excludedFeatureIds: string[],
                jobMapLayerCacheTimestamp: string,
                bounds?: L.LatLngBounds,
                layerLookupFn?: (layerId: string) => Layer,
                styleOverrideFields?: string
            ) {
                // Determine if this is a job-based layer or single layer
                const isJobBasedLayer = !!(layerModel as MapJob).id && !(layerModel as Layer).styleId;
                const isLayerBasedLayer = !!(layerModel as Layer).id && !!(layerModel as Layer).styleId;
                const jobClusterColorKey = '000000';

                let tileClusterLayerThis: any;

                if (isLayerBasedLayer) {
                    // Single layer mode (existing behavior)
                    const layer = layerModel as Layer;

                    (L.ClusterLayer.prototype as any).initialize.call(
                        this,
                        layer.id,
                        layer.getLayerColorKey() || null,
                        zIndex,
                        true // enableClustering
                    );

                    this.stop = new Subject<void>();
                    tileClusterLayerThis = this;

                    tileClusterLayerThis.layer = layer;
                    tileClusterLayerThis.isJobBasedLayer = false;

                    // Single layer lookup: always return the same layer
                    tileClusterLayerThis.getLayerById = () => layer;
                } else if (isJobBasedLayer) {
                    // Job-based layer mode (new)
                    const mapJob = layerModel as MapJob;

                    (L.ClusterLayer.prototype as any).initialize.call(
                        this,
                        mapJob.id, // Use job ID as layer identifier
                        jobClusterColorKey, // No single color key for multi-layer jobs
                        zIndex,
                        true // enableClustering
                    );

                    this.stop = new Subject<void>();
                    tileClusterLayerThis = this;

                    tileClusterLayerThis.mapJob = mapJob;
                    tileClusterLayerThis.layer = null; // No single layer reference
                    tileClusterLayerThis.isJobBasedLayer = true;
                    tileClusterLayerThis.jobBounds = bounds || mapJob.bounds;

                    // Job layer lookup: use provided function (delegates to LayersStore)
                    tileClusterLayerThis.getLayerById = layerLookupFn || ((layerId: string): Layer | null => null);
                }

                // features managed by the tileclusterlayer (excludes those managed by base clusterLayer)
                tileClusterLayerThis.tileClusterLayerFeatureIds = [];

                let prefixPath = '/projects/' + projectId;
                let tilePath = env.tilesApiUrl + prefixPath + '/tiles/{z}/{x}/{y}';

                // features that must be excluded from the map layer (e.g. they are displayed in the tasks layer)
                tileClusterLayerThis.excludedFeatureIds = excludedFeatureIds || [];
                // track the features that are the map layer response (i.e. not in a server cluster) but are excluded
                // so that we add then when they are unexcluded
                tileClusterLayerThis.existingButExcludedFeatureIdsAtThisZoomLevel = [];

                // Server-side Filter: Build filter request
                let filter = new FullFeatureFilter(applyFilter);

                if (tileClusterLayerThis.isJobBasedLayer) {
                    // Job-based layer: Filter by specific jobId
                    filter.jobIds = [tileClusterLayerThis.mapJob.id];
                    // Clear job visibility filters - they're not needed for individual job tile layers
                    // Job visibility is handled at the layer creation level, not feature filtering level
                    filter.visibleJobIds = [];
                    filter.hiddenJobIds = [];
                    filter.hideAllJobs = false;
                    // Do NOT set filter.layers - we want all layers for this job
                } else {
                    // Single layer mode: Filter by specific layer
                    filter.layers = [tileClusterLayerThis.layer];
                }

                let filterRequest = filter.buildFilterRequest();
                tilePath += `?tileSize=${TILE_CONFIG.tileSizeParam}&filter=` + JSON.stringify(filterRequest);

                // Fields CSV: For both modes, pass the layer or mapJob to getFieldCsv
                let fieldsParam = this.getFieldCsv(tileClusterLayerThis.layer || tileClusterLayerThis.mapJob);

                // For job-based layers, add metadata.common_layerId to identify which layer each feature belongs to
                if (tileClusterLayerThis.isJobBasedLayer) {
                    fieldsParam = fieldsParam
                        ? fieldsParam + `${styleOverrideFields ? ',' + styleOverrideFields : ''},metadata.${TemplatedFeatureMetadataProperty.COMMON_LAYER_ID}`
                        : `${styleOverrideFields ? styleOverrideFields : ''},metadata.${TemplatedFeatureMetadataProperty.COMMON_LAYER_ID}`;
                }
                if (fieldsParam) {
                    tilePath += '&fields=' + fieldsParam;
                }

                tilePath += '&timestamp={timeStamp}';

                tileClusterLayerThis.symbologyMarkerLayerGroupCache = new Map<string, L.LayerGroup>();

                // Create GeoJSON Tile Layer
                // See tile-config.ts for detailed flow chart of how tilePath and tileLayerOptions work together
                this.tileLayer = new (L.TileLayer as any).GeoJSON(
                    tilePath, // Parameter 1: URL template (WHERE to get data)
                    _.extend({}, tileLayerOptions, {
                        // Parameter 2: Tile options (HOW to request/render)
                        subdomains: [],
                        zIndex: zIndex,
                        bounds: tileClusterLayerThis.jobBounds || tileClusterLayerThis.layer?.bounds,
                        timeStamp: tileClusterLayerThis.layer?.cacheTimeStamp || jobMapLayerCacheTimestamp || '',
                        unique: (feature: Feature) => feature.id
                    }),
                    {
                        // NOTE: The following option functions are listed in the order they are called

                        // 1. Filter features from the tile - redirect clusterable features off to the markerClusterGroup
                        filter: (feature: Feature): boolean => {
                            let existingFeatureMapLayer = featureMapLayerCacheService.getFeatureMapLayer(feature.id);
                            let existingFeature = cachedFeatureService.getFeature(feature.id);
                            if (existingFeature && existingFeature.deleted) {
                                return false;
                            }
                            if (tileClusterLayerThis.isClusterable(feature)) {
                                if (
                                    !existingFeatureMapLayer ||
                                    !tileClusterLayerThis.markerClusterOrLayerGroup.hasLayer(existingFeatureMapLayer)
                                ) {
                                    // only create new feature map layer if not excluded
                                    if (tileClusterLayerThis.excludedFeatureIds.indexOf(feature.id) < 0) {
                                        // Get the correct layer for this feature dynamically
                                        const featureLayer = tileClusterLayerThis.getLayerForFeature(feature);

                                        if (featureLayer) {
                                            feature.layerId = featureLayer.id;
                                            tileClusterLayerThis.layerColorKey = featureLayer.getLayerColorKey();

                                            if (featureLayer.style) {
                                                feature.style = featureLayer.style;
                                            }
                                            let featureMapLayer = L.ClusterLayer.prototype.createOrUpdateMarkers.call(
                                                tileClusterLayerThis,
                                                feature,
                                                tileClusterLayerThis.isJobBasedLayer
                                                    ? tileClusterLayerThis.mapJob.id
                                                    : featureLayer.id,
                                                tileClusterLayerThis.layerColorKey
                                            );
                                            tileClusterLayerThis.pendingFeatureMapLayers.push(featureMapLayer);
                                        } else if (tileClusterLayerThis.isJobBasedLayer) {
                                            let featureMapLayer = L.ClusterLayer.prototype.createOrUpdateMarkers.call(
                                                tileClusterLayerThis,
                                                feature,
                                                tileClusterLayerThis.mapJob.id,
                                                jobClusterColorKey
                                            );
                                            tileClusterLayerThis.pendingFeatureMapLayers.push(featureMapLayer);
                                        }
                                    } else {
                                        if (
                                            tileClusterLayerThis.existingButExcludedFeatureIdsAtThisZoomLevel.indexOf(
                                                feature.id
                                            ) < 0
                                        ) {
                                            tileClusterLayerThis.existingButExcludedFeatureIdsAtThisZoomLevel.push(
                                                feature.id
                                            );
                                        }
                                    }
                                }
                                return false;
                            } else {
                                // don't allow excluded features to be added
                                if (tileClusterLayerThis.excludedFeatureIds.indexOf(feature.id) >= 0) {
                                    return false;
                                }

                                // Note: only manages features not sent to the clusterLayer
                                if (tileClusterLayerThis.tileClusterLayerFeatureIds.indexOf(feature.id) < 0) {
                                    tileClusterLayerThis.tileClusterLayerFeatureIds.push(feature.id);
                                }
                                return true;
                            }
                        },

                        // 2. Convert from GeoJSON coords to WGS84
                        coordsToLatLng: (coords: any): any => {
                            let crs: any;
                            let epsg = '4326'; // todo - get from layer
                            switch (epsg) {
                                case '3857':
                                    crs = L.CRS.EPSG3857;
                                    crs.unproject = function (point: any) {
                                        // don't ask!
                                        let earthRadius = 6378137;
                                        let normalizedPoint = point.divideBy(earthRadius);
                                        return this.projection.unproject(normalizedPoint);
                                    };
                                    break;
                                case '4326':
                                    crs = L.CRS.EPSG4326;
                                    crs.unproject = function (point: any) {
                                        return this.projection.unproject(point);
                                    };
                                    break;
                                default:
                                    log.warn('Unsupported EPSG ' + epsg);
                                    break;
                            }
                            return crs.unproject(new L.Point(coords[0], coords[1]));
                        },

                        // 3. Generally unused (because points are clustered),
                        // except when dealing with points as part of a GeometryCollection
                        /*pointToLayer: function(feature) {
                          // Note: for GeometryCollection features we only get the "sub"-feature with no id and symbology properties.
                          //  So we utilize the saved currentFeature to provide this info.
                          feature = cachedFeatureService.updateFromCacheFeature(feature);
                          featureMapLayersService.setLayerSymbologyProperties(feature, tileClusterLayerThis.layerColorKey);
                          var childMapLayer = featureMapLayersService.createFeatureMapLayer(feature);
                          var existingFeatureMapLayer = featureMapLayerCacheService.getFeatureMapLayer(feature.id);
                          var mapLayer = featureMapLayersService.mergeChildToParentFeatureMapLayer(existingFeatureMapLayer, childMapLayer);
                          if (mapLayer) {
                            featureMapLayerCacheService.addOrUpdateFeatureMapLayer(feature, mapLayer, layerModel);
                          }
                          return childMapLayer;
                        },*/

                        // 4. Determine line or polygon style or the feature
                        style: (feature: Feature): L.PathOptions => {
                            // feature is always an "incomplete" feature (id, geometry) - We need to get selection status,
                            //  etc. from full feature in feature cache and from the layer
                            const featureLayer = tileClusterLayerThis.getLayerForFeature(feature);
                            featureMapLayersService.setLayerSymbologyProperties(feature, featureLayer);
                            return featureMapLayersService.getFeatureStyle(feature);
                        },

                        // 5. Track each feature added via the tile
                        onEachFeature: (feature: Feature, featureMapLayer: FeatureMapLayer): void => {
                            // Get the correct layer for this feature dynamically
                            const featureLayer = tileClusterLayerThis.getLayerForFeature(feature);

                            if (featureLayer) {
                                feature.layerId = featureLayer.id;
                                feature.style = featureLayer.style;

                                // Note: this may be just one part of a clipped feature
                                let existingFeatureMapLayer = featureMapLayerCacheService.getFeatureMapLayer(
                                    feature.id
                                );

                                if (existingFeatureMapLayer) {
                                    featureMapLayer = featureMapLayersService.mergeFeatureMapLayers(
                                        existingFeatureMapLayer,
                                        featureMapLayer
                                    ) as FeatureMapLayer;
                                }
                                if (featureMapLayer) {
                                    feature = cachedFeatureService.updateFromCacheFeature(feature);
                                    feature.jobId = tileClusterLayerThis?.mapJob?.id;
                                    featureMapLayersService.updateFeatureMapLayer(feature, featureMapLayer, false);
                                }

                                // Custom symbology for lines. Leaflet supports svg icons for markers but not for lines and polylines.
                                // So, we add svg markers along the line.
                                featureMapLayersService.refreshSymbologyMarkerIcons(
                                    feature,
                                    markerClusterGroupOptions.chunkDelay
                                );
                            }
                        }
                    },
                    (req: string) => throttledHttp.queueRequest(req)
                );

                tileClusterLayerThis.zoom = -1;
                tileClusterLayerThis.pendingFeatureMapLayers = [];

                // event handlers
                map.on('zoom', this.onZoom.bind(tileClusterLayerThis));
                map.on('zoomend', this.onZoomEnd.bind(tileClusterLayerThis));
                tileClusterLayerThis.tileLayer.on('loading', this.onLayerLoading.bind(tileClusterLayerThis));
                tileClusterLayerThis.tileLayer.on('load', this.onLayerLoaded.bind(tileClusterLayerThis));
            },

            isClusterable: (feature: Feature): boolean => L.ClusterLayer.prototype.isClusterable(feature),

            /**
             * Get layer for a feature dynamically
             * For job layers: lookup via getLayerById using feature.metadata.common_layerId
             * For single layer: return the layer reference
             */
            getLayerForFeature(feature: Feature): Layer | null {
                if (this.isJobBasedLayer) {
                    // Dynamic lookup via provided function
                    const layerId = feature.metadata?.common_layerId || feature.layerId;
                    if (layerId && this.getLayerById) {
                        return this.getLayerById(layerId);
                    }
                    return null;
                } else {
                    // Single layer mode - return layer reference
                    return this.layer;
                }
            },

            addExcludedFeature(feature: Feature): void {
                if (this.excludedFeatureIds.indexOf(feature.id) < 0) {
                    this.excludedFeatureIds.push(feature.id);
                    this.handleExcludedFeature(feature);
                }
            },

            removeExcludedFeature(feature: Feature): void {
                let index = this.excludedFeatureIds.indexOf(feature.id);
                if (index >= 0) {
                    this.excludedFeatureIds.splice(index, 1);
                    this.handleExcludedFeature(feature);
                }
            },

            handleExcludedFeature(feature: Feature): void {
                // Note: works for both clustered and non-clustered features on layer
                let isClusterable: boolean = this.isClusterable(feature);
                if (feature) {
                    let existingFeatureMapLayer = featureMapLayerCacheService.getFeatureMapLayer(feature.id);
                    if (existingFeatureMapLayer) {
                        if (
                            // is excluded but exists...
                            this.excludedFeatureIds.indexOf(feature.id) >= 0 &&
                            this.hasLayerForFeature(feature, existingFeatureMapLayer)
                        ) {
                            // ...so remove
                            this.removeLayerForFeature(feature, existingFeatureMapLayer);
                            if (isClusterable) {
                                if (this.existingButExcludedFeatureIdsAtThisZoomLevel.indexOf(feature.id) < 0) {
                                    this.existingButExcludedFeatureIdsAtThisZoomLevel.push(feature.id);
                                }
                            }
                        } else if (
                            // is NOT excluded but doesn't exist...
                            this.excludedFeatureIds.indexOf(feature.id) < 0 &&
                            !this.hasLayerForFeature(feature, existingFeatureMapLayer)
                        ) {
                            if (isClusterable) {
                                let index = this.existingButExcludedFeatureIdsAtThisZoomLevel.indexOf(feature.id);
                                if (index >= 0) {
                                    // ...and was existing at this zoom level ...so add
                                    this.existingButExcludedFeatureIdsAtThisZoomLevel.splice(index, 1);
                                    this.addLayerForFeature(feature, existingFeatureMapLayer);
                                }
                            } else {
                                this.addLayerForFeature(feature, existingFeatureMapLayer);
                            }
                        }
                    }
                }
            },

            hasLayerForFeature(feature: Feature, featureMapLayer: FeatureMapLayer): boolean {
                if (this.isClusterable(feature)) {
                    return L.ClusterLayer.prototype.hasLayer.call(this, featureMapLayer);
                } else {
                    return this.tileClusterLayerFeatureIds.indexOf(feature.id) >= 0;
                }
            },

            addLayerForFeature(feature: Feature, featureMapLayer: FeatureMapLayer): void {
                if (this.isClusterable(feature)) {
                    L.ClusterLayer.prototype.addLayers.call(this, [featureMapLayer]);
                } else {
                    this.tileLayer.addLayerForFeature(feature.id, featureMapLayer);
                    if (this.tileClusterLayerFeatureIds.indexOf(feature.id) < 0) {
                        this.tileClusterLayerFeatureIds.push(feature.id);
                    }
                }
            },

            removeLayerForFeature(feature: Feature, featureMapLayer: FeatureMapLayer): void {
                if (this.isClusterable(feature)) {
                    L.ClusterLayer.prototype.removeLayers.call(this, [featureMapLayer]);
                } else {
                    this.tileLayer.removeLayerForFeature(feature.id);
                    let index = this.tileClusterLayerFeatureIds.indexOf(feature.id);
                    if (index >= 0) {
                        this.tileClusterLayerFeatureIds.splice(index, 1);
                    }
                }
            },

            onAdd(map: L.Map): void {
                L.ClusterLayer.prototype.onAdd.call(this, map);
                map.addLayer(this.tileLayer);
            },

            onRemove(map: L.Map): void {
                map.removeLayer(this.tileLayer);
                L.ClusterLayer.prototype.onRemove.call(this, map);
                this.clearLayer();
            },

            setZIndex(zIndex: number): void {
                L.ClusterLayer.prototype.setZIndex.call(this, zIndex);

                this.tileLayer.setZIndex(zIndex);
            },

            redraw(): void {
                this.clearLayer();
                this.tileLayer.redraw();
            },

            onZoom(event: { target: any }): void {
                // The entire layer is reloaded whenever the zoom changes
                let newZoom = event.target.getZoom();
                if (this.zoom !== newZoom) {
                    this.zoom = newZoom;
                }
            },

            onZoomEnd(event: { target: any }): void {
                this.clearLayer();
            },

            onLayerLoading(event: { target: any }): void {
                // Only report loading state for single layer mode, not for job-based layers
                if (!this.isJobBasedLayer && this.layer) {
                    layersStreams.setLayerAsLoading(this.layer);
                }
                this.addPendingFeatureMapLayers();
            },

            onLayerLoaded(/*event*/): void {
                this.cancelTimer();
                this.addPendingFeatureMapLayers();
            },

            clearLayer(): void {
                this.cancelTimer();
                L.ClusterLayer.prototype.clearLayers.call(this);
                this.pendingFeatureMapLayers = [];
                this.existingButExcludedFeatureIdsAtThisZoomLevel = [];
                this.tileClusterLayerFeatureIds = [];

                // Remove SVG Marker layers for clustered or other non-visible features.
                this.symbologyMarkerLayerGroupCache.forEach((layerGroup: L.LayerGroup, key: string) => {
                    if (this.tileClusterLayerFeatureIds.indexOf(key) < 0) {
                        layerGroup.remove();
                        this.symbologyMarkerLayerGroupCache.delete(key);
                    }
                });
            },

            cancelTimer(): void {
                this.stop.next(null);
                // Only report loaded state for single layer mode, not for job-based layers
                if (!this.isJobBasedLayer && this.layer) {
                    layersStreams.setLayerAsLoaded(this.layer);
                }
            },

            addPendingFeatureMapLayers(): void {
                let featureMapLayers: FeatureMapLayer[] = this.pendingFeatureMapLayers.slice(); // make shallow copy
                this.pendingFeatureMapLayers = [];
                L.ClusterLayer.prototype.addLayers.call(this, featureMapLayers);
            },

            getStyleOverrideFields(styleOverrides: SymbologyOverrideChoice[]): string[] {
                if (!styleOverrides?.length) {
                    return [];
                }

                const fields = new Set(styleOverrides.map(styleOverride => 'properties.' + styleOverride.choiceField));
                return Array.from(fields);
            },

            getLabelField(layerModel: Layer | MapJob): string {
                if (!layerModel.id) return '';

                // Get workspace settings with null check
                const workspaceSettings = userSettingsStreamService?.getCurrentMapWorkspaceSettings();
                if (!workspaceSettings) return '';

                // dictionary of layerId to label
                const layerIdLabelMapSettings = workspaceSettings?.layerIdLabelMap;
                if (!layerIdLabelMapSettings) return '';
                if (layerModel instanceof MapJob) {
                    const allLabelFields = Object.values(layerIdLabelMapSettings)
                        .filter(Boolean)
                        .map(label => `properties.${label}`)
                        .join(',');

                    return allLabelFields;
                }
                // check layerid against the dictionary
                const labelVal = layerIdLabelMapSettings[layerModel.id];
                if (!labelVal) return '';

                const label = labelVal ? `properties.${labelVal}` : '';
                return label;
            },

            getFieldCsv(layerModel: Layer | MapJob): string {
                let styleOverridesArray: string[] = [];
                const labelField = this.getLabelField(layerModel);

                if (layerModel instanceof Layer) {
                    styleOverridesArray = this.getStyleOverrideFields(layerModel.style?.styleOverrides);
                }

                // compare labelField and styleOverrides and make them unique
                const uniqueFields = new Set<string>([...styleOverridesArray, labelField].filter(Boolean));

                // convert array to string
                const fields = Array.from(uniqueFields).join(',');

                return fields;
            }
        });
    }
}
